/**
 * Zipf frequency bands for reader highlighting (green = most frequent).
 * Thresholds are lower bounds for each band; the last band is everything below the last threshold.
 */

import { occurrencePosMeta } from "./lexicon-pos-abbrev.js";

/** Number of range sliders / lower-bound cutoffs (nine bands = this + 1). */
export const LEXICON_ZIPF_THRESHOLD_COUNT = 8;

/** Descending lower bounds: band 0 is zipf >= ZIPF_THRESHOLDS[0], etc. */
export const ZIPF_THRESHOLDS = [6.15, 5.55, 4.95, 4.35, 3.75, 3.15, 2.55, 1.95];

/**
 * Rough CEFR-style sub-level labels (for legend). Length is {@link LEXICON_ZIPF_THRESHOLD_COUNT} + 1
 * (one extra band for rarer words below the last cutoff).
 */
export const ZIPF_BAND_LEVEL_KEYS = [
  "A1.1",
  "A1.2",
  "A2.1",
  "A2.2",
  "B1.1",
  "B1.2",
  "B2.1",
  "B2.2",
  "C1/C2",
];

/**
 * Mid-band Zipf for server fine-level ids (aligned with `lexicon/src/levels/fine.ts` cutoffs).
 * Used when the Lexicon API returns `level` without numeric `zipf` (e.g. content lexicon overlay).
 *
 * @param {unknown} level
 * @returns {number} NaN when unknown
 */
export function fineLevelToRepresentativeZipf(level) {
  /** @type {Record<string, number>} */
  var map = {
    "A1.1": 6.5,
    "A1.2": 5.65,
    "A2.1": 4.925,
    "A2.2": 4.55,
    "B1.1": 4.35,
    "B1.2": 4.25,
    "B2.1": 4.15,
    "B2.2": 4.075,
    "C1/C2": 3.7,
    "C1+": 3.7,
  };
  var s = level == null ? "" : String(level).trim();
  if (!s) return NaN;
  var z = map[s];
  return typeof z === "number" && Number.isFinite(z) ? z : NaN;
}

/** localStorage key for {@link getLexiconFrequencySource}. */
export const LEXICON_FREQUENCY_SOURCE_KEY = "ll_lexicon_frequency_source";

/** Dispatched on `document` when the user switches Zipf coloring source (bubbles). */
export const LEXICON_FREQUENCY_SOURCE_EVENT = "ll-lexicon-frequency-source";

/** localStorage key for {@link getZipfThresholds}. */
export const LEXICON_ZIPF_THRESHOLDS_STORAGE_KEY = "ll_lexicon_zipf_thresholds_v1";

/** Dispatched when Zipf band thresholds change (sliders, reset, or another tab). */
export const LEXICON_ZIPF_THRESHOLDS_EVENT = "ll-lexicon-zipf-thresholds";

var ZIPF_SLIDER_MIN = 1;
var ZIPF_SLIDER_MAX = 8;
var ZIPF_THRESH_GAP = 0.05;

/**
 * Expand a legacy 5-cutoff array (six bands) to eight cutoffs (nine bands) without losing user tuning.
 * @param {number[]} oldFive
 * @returns {number[]}
 */
function expandLegacyFiveThresholds(oldFive) {
  var o = oldFive.map(function(x) {
    return Number(x);
  });
  return [
    o[0],
    (o[0] + o[1]) / 2,
    o[1],
    (o[1] + o[2]) / 2,
    o[2],
    (o[2] + o[3]) / 2,
    o[3],
    (o[3] + o[4]) / 2,
  ];
}

/**
 * @param {unknown} raw parsed JSON array from storage
 * @returns {number[]|null} length {@link LEXICON_ZIPF_THRESHOLD_COUNT}, or null to fall back to defaults
 */
function normalizeStoredThresholdArray(raw) {
  if (!Array.isArray(raw)) return null;
  if (raw.length === LEXICON_ZIPF_THRESHOLD_COUNT) {
    var same = [];
    for (var s = 0; s < raw.length; s++) same.push(raw[s]);
    return same;
  }
  if (raw.length === 5) return expandLegacyFiveThresholds(raw);
  return null;
}

/**
 * @param {number[]|null|undefined} raw
 * @returns {number[]} {@link LEXICON_ZIPF_THRESHOLD_COUNT} strictly descending values in [min, max]
 */
function sanitizeThresholdsArray(raw) {
  var def = ZIPF_THRESHOLDS;
  var gap = ZIPF_THRESH_GAP;
  var min = ZIPF_SLIDER_MIN;
  var max = ZIPF_SLIDER_MAX;
  var n = LEXICON_ZIPF_THRESHOLD_COUNT;
  var out = [];
  for (var i = 0; i < n; i++) {
    var v0 = raw != null && raw[i] != null ? Number(raw[i]) : def[i];
    out.push(Number.isFinite(v0) ? v0 : def[i]);
  }
  for (var c = 0; c < 48; c++) {
    for (var j = 0; j < n; j++) {
      out[j] = Math.min(max, Math.max(min, out[j]));
    }
    var changed = false;
    for (var u = 1; u < n; u++) {
      if (out[u] >= out[u - 1]) {
        out[u] = out[u - 1] - gap;
        changed = true;
      }
    }
    for (var v = n - 2; v >= 0; v--) {
      if (out[v] <= out[v + 1]) {
        out[v] = out[v + 1] + gap;
        changed = true;
      }
    }
    if (!changed) break;
  }
  for (var r = 0; r < n; r++) {
    out[r] = Math.round(out[r] * 100) / 100;
  }
  return out;
}

/**
 * Persisted lower Zipf bounds for bands (see {@link zipfToBand}). Defaults match {@link ZIPF_THRESHOLDS}.
 * @returns {number[]}
 */
export function getZipfThresholds() {
  try {
    if (typeof localStorage === "undefined") return sanitizeThresholdsArray(ZIPF_THRESHOLDS);
    var stored = localStorage.getItem(LEXICON_ZIPF_THRESHOLDS_STORAGE_KEY);
    if (!stored) return sanitizeThresholdsArray(ZIPF_THRESHOLDS);
    var arr = JSON.parse(stored);
    var normalized = normalizeStoredThresholdArray(arr);
    if (!normalized) return sanitizeThresholdsArray(ZIPF_THRESHOLDS);
    return sanitizeThresholdsArray(normalized);
  } catch (e) {
    return sanitizeThresholdsArray(ZIPF_THRESHOLDS);
  }
}

/**
 * @param {number[]} thresholds length {@link LEXICON_ZIPF_THRESHOLD_COUNT} (sanitized)
 * @returns {number[]}
 */
export function saveZipfThresholds(thresholds) {
  var t = sanitizeThresholdsArray(thresholds);
  try {
    if (typeof localStorage !== "undefined") localStorage.setItem(LEXICON_ZIPF_THRESHOLDS_STORAGE_KEY, JSON.stringify(t));
  } catch (e) {
    /* ignore */
  }
  try {
    document.dispatchEvent(new CustomEvent(LEXICON_ZIPF_THRESHOLDS_EVENT, { bubbles: true }));
  } catch (e2) {
    /* ignore */
  }
  return t;
}

export function resetZipfThresholdsToDefaults() {
  try {
    if (typeof localStorage !== "undefined") localStorage.removeItem(LEXICON_ZIPF_THRESHOLDS_STORAGE_KEY);
  } catch (e) {
    /* ignore */
  }
  try {
    document.dispatchEvent(new CustomEvent(LEXICON_ZIPF_THRESHOLDS_EVENT, { bubbles: true }));
  } catch (e2) {
    /* ignore */
  }
}

/**
 * @param {number[]|null|undefined} current from {@link getZipfThresholds}
 * @param {number} index 0 .. {@link LEXICON_ZIPF_THRESHOLD_COUNT} - 1
 * @param {unknown} rawVal slider value
 * @returns {number[]}
 */
export function clampZipfThresholdChange(current, index, rawVal) {
  var t = sanitizeThresholdsArray(current);
  var i = Number(index) || 0;
  var n = LEXICON_ZIPF_THRESHOLD_COUNT;
  if (i < 0 || i > n - 1) return t;
  var gap = ZIPF_THRESH_GAP;
  var lo = ZIPF_SLIDER_MIN;
  var hi = ZIPF_SLIDER_MAX;
  if (i < n - 1) lo = Math.max(lo, t[i + 1] + gap);
  if (i > 0) hi = Math.min(hi, t[i - 1] - gap);
  var v = Number(rawVal);
  if (!Number.isFinite(v)) return t;
  v = Math.min(hi, Math.max(lo, v));
  t[i] = v;
  return sanitizeThresholdsArray(t);
}

/**
 * @returns {"combined"|"wordfreq"|"corpus"|"dwds"}
 */
export function getLexiconFrequencySource() {
  try {
    if (typeof localStorage !== "undefined") {
      var raw = localStorage.getItem(LEXICON_FREQUENCY_SOURCE_KEY);
      if (raw === "corpus") return "corpus";
      if (raw === "wordfreq") return "wordfreq";
      if (raw === "dwds") return "dwds";
    }
  } catch (e) {
    /* ignore */
  }
  return "combined";
}

/**
 * @param {"combined"|"wordfreq"|"corpus"|"dwds"} source
 */
export function setLexiconFrequencySource(source) {
  var v =
    source === "corpus"
      ? "corpus"
      : source === "wordfreq"
        ? "wordfreq"
        : source === "dwds"
          ? "dwds"
          : "combined";
  try {
    if (typeof localStorage !== "undefined") localStorage.setItem(LEXICON_FREQUENCY_SOURCE_KEY, v);
  } catch (e) {
    /* ignore */
  }
  try {
    if (typeof document !== "undefined") {
      document.dispatchEvent(new CustomEvent(LEXICON_FREQUENCY_SOURCE_EVENT, { bubbles: true }));
    }
  } catch (e2) {
    /* ignore */
  }
}

/**
 * Use before showing Zipf / per-million in UI. Avoids treating `null` as 0 (`Number(null) === 0`).
 * @param {unknown} value
 * @returns {boolean}
 */
export function isLexiconMetricRenderable(value) {
  if (value === null || typeof value === "undefined") return false;
  if (typeof value === "string" && !String(value).trim()) return false;
  return Number.isFinite(Number(value));
}

/**
 * @param {Record<string, unknown>} r
 * @returns {number}
 */
function corpusZipfFromRow(r) {
  var cz = r.corpusZipf != null ? r.corpusZipf : r.corpus_zipf;
  var zn = Number(cz);
  if (Number.isFinite(zn)) return zn;
  var cf =
    r.corpusFrequencyPerMillion != null
      ? r.corpusFrequencyPerMillion
      : r.corpus_frequency_per_million != null
        ? r.corpus_frequency_per_million
        : null;
  var n = Number(cf);
  if (Number.isFinite(n) && n > 0) return Math.log10(n) + 3;
  var zLv = fineLevelToRepresentativeZipf(r.level);
  if (Number.isFinite(zLv)) return zLv;
  return NaN;
}

/**
 * Wordfreq / reference Zipf from a lexicon occurrence row (ignores UI frequency source).
 * @param {Record<string, unknown>} r
 * @returns {number}
 */
function wordfreqZipfFromRow(r) {
  var zPick = r.zipf != null ? r.zipf : r.Zipf;
  var z0 = Number(zPick);
  if (Number.isFinite(z0)) return z0;
  var zFromLevel = fineLevelToRepresentativeZipf(r.level != null ? r.level : r.fineLevel);
  if (Number.isFinite(zFromLevel)) return zFromLevel;
  var nest = r.stats;
  if (nest && typeof nest === "object") {
    var zs = nest.zipf != null ? nest.zipf : nest.Zipf;
    var z1 = Number(zs);
    if (Number.isFinite(z1)) return z1;
  }
  var fpm =
    r.frequencyPerMillion != null
      ? r.frequencyPerMillion
      : r.frequencypermillion != null
        ? r.frequencypermillion
        : r.FrequencyPerMillion != null
          ? r.FrequencyPerMillion
          : r.frequency_per_million;
  var n = Number(fpm);
  if (!Number.isFinite(n) || n <= 0) return NaN;
  return Math.log10(n) + 3;
}

/**
 * @param {Record<string, unknown>} r
 * @returns {number|null}
 */
function combinedFrequencyPerMillionFromRow(r) {
  var v = r.combinedFrequency != null ? r.combinedFrequency : r.combined_frequency;
  var n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * @param {Record<string, unknown>} r
 * @returns {number|null}
 */
function dwdsFrequencyPerMillionFromRow(r) {
  var v = r.dwdsFrequency != null ? r.dwdsFrequency : r.dwds_frequency;
  var n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/**
 * @param {Record<string, unknown>} r
 * @returns {number}
 */
function combinedZipfFromRow(r) {
  var cz = r.combinedZipf != null ? r.combinedZipf : r.combined_zipf;
  var zn = Number(cz);
  if (Number.isFinite(zn)) return zn;
  var cf = combinedFrequencyPerMillionFromRow(r);
  if (cf != null && cf > 0) return Math.log10(cf) + 3;
  var zLv = fineLevelToRepresentativeZipf(r.level);
  if (Number.isFinite(zLv)) return zLv;
  return NaN;
}

/**
 * @param {Record<string, unknown>} r
 * @returns {number}
 */
function dwdsZipfFromRow(r) {
  var dz = r.dwdsZipf != null ? r.dwdsZipf : r.dwds_zipf;
  var zn = Number(dz);
  if (Number.isFinite(zn)) return zn;
  var df = dwdsFrequencyPerMillionFromRow(r);
  if (df != null && df > 0) return Math.log10(df) + 3;
  var zLv = fineLevelToRepresentativeZipf(r.level);
  if (Number.isFinite(zLv)) return zLv;
  return NaN;
}

/**
 * Zipf scores for word-detail UI (independent of {@link getLexiconFrequencySource}).
 * @param {Record<string, unknown>|null|undefined} row
 * @returns {{ wordfreqZipf: number, corpusZipf: number, combinedZipf: number, dwdsZipf: number }}
 */
export function occurrenceZipfBreakdown(row) {
  if (!row || typeof row !== "object") {
    return { wordfreqZipf: NaN, corpusZipf: NaN, combinedZipf: NaN, dwdsZipf: NaN };
  }
  /** @type {any} */
  var r = row;
  return {
    wordfreqZipf: wordfreqZipfFromRow(r),
    corpusZipf: corpusZipfFromRow(r),
    combinedZipf: combinedZipfFromRow(r),
    dwdsZipf: dwdsZipfFromRow(r),
  };
}

/**
 * Zipf score from a per-occurrence or per-surface lexicon row.
 * Uses **combined** (`combinedZipf` / `combinedFrequency`), **DWDS** (`dwdsZipf` / `dwdsFrequency`),
 * **wordfreq** (`zipf` / `frequencyPerMillion`), or **corpus** (`corpusZipf` / `corpusFrequencyPerMillion`)
 * depending on {@link getLexiconFrequencySource}.
 *
 * @param {Record<string, unknown>|null|undefined} row
 * @returns {number}
 */
export function occurrenceZipfNumeric(row) {
  if (!row || typeof row !== "object") return NaN;
  /** @type {any} */
  var r = row;
  var src = getLexiconFrequencySource();
  if (src === "corpus") {
    return corpusZipfFromRow(r);
  }
  if (src === "dwds") {
    var dw = dwdsZipfFromRow(r);
    if (Number.isFinite(dw)) return dw;
  }
  if (src === "combined") {
    var comb = combinedZipfFromRow(r);
    if (Number.isFinite(comb)) return comb;
  }
  return wordfreqZipfFromRow(r);
}

/**
 * Raw per-million count from a lexicon occurrence row (when present).
 * @param {Record<string, unknown>|null|undefined} row
 * @returns {number|null}
 */
export function occurrenceFrequencyPerMillionRaw(row) {
  if (!row || typeof row !== "object") return null;
  /** @type {any} */
  var r = row;
  var src = getLexiconFrequencySource();
  if (src === "corpus") {
    var cf =
      r.corpusFrequencyPerMillion != null
        ? r.corpusFrequencyPerMillion
        : r.corpus_frequency_per_million != null
          ? r.corpus_frequency_per_million
          : null;
    var n0 = Number(cf);
    return Number.isFinite(n0) ? n0 : null;
  }
  if (src === "combined") {
    var cfm = combinedFrequencyPerMillionFromRow(r);
    return cfm;
  }
  if (src === "dwds") {
    return dwdsFrequencyPerMillionFromRow(r);
  }
  var fpm =
    r.frequencyPerMillion != null
      ? r.frequencyPerMillion
      : r.frequencypermillion != null
        ? r.frequencypermillion
        : r.FrequencyPerMillion != null
          ? r.FrequencyPerMillion
          : r.frequency_per_million;
  var n = Number(fpm);
  return Number.isFinite(n) ? n : null;
}

/**
 * Zipf + optional per-million for word panels (from `text.json` occurrence rows).
 * @param {Record<string, unknown>|null|undefined} row
 * @returns {{
 *   zipf: number,
 *   frequencyPerMillion: number|null,
 *   wordfreqZipf: number,
 *   corpusZipf: number,
 *   combinedZipf: number,
 *   dwdsZipf: number,
 *   combinedFrequencyPerMillion: number|null,
 *   dwdsFrequencyPerMillion: number|null,
 *   posAbbrev: string,
 *   posNote: string,
 *   posLookupKey: string,
 * }}
 */
export function occurrenceLexiconFrequencyMeta(row) {
  var z = occurrenceZipfNumeric(row);
  var fpm = occurrenceFrequencyPerMillionRaw(row);
  var bd = occurrenceZipfBreakdown(row);
  var combFm = null;
  var dwdsFm = null;
  var pm = occurrencePosMeta(row);
  if (row && typeof row === "object") {
    /** @type {any} */
    var rx = row;
    combFm = combinedFrequencyPerMillionFromRow(rx);
    dwdsFm = dwdsFrequencyPerMillionFromRow(rx);
  }
  var levelLabel = "";
  if (row && typeof row === "object") {
    /** @type {any} */
    var rxLv = row;
    if (rxLv.level != null && String(rxLv.level).trim()) {
      levelLabel = String(rxLv.level).trim().toUpperCase();
    }
  }
  return {
    zipf: z,
    frequencyPerMillion: fpm,
    wordfreqZipf: bd.wordfreqZipf,
    corpusZipf: bd.corpusZipf,
    combinedZipf: bd.combinedZipf,
    dwdsZipf: bd.dwdsZipf,
    combinedFrequencyPerMillion: combFm,
    dwdsFrequencyPerMillion: dwdsFm,
    posAbbrev: pm.posAbbrev,
    posNote: pm.posNote,
    posLookupKey: pm.posLookupKey,
    level: levelLabel,
  };
}

/**
 * Fills in POS fields on {@link occurrenceLexiconFrequencyMeta} when the occurrence row lacks them
 * but a lemma sheet row or lexicon-table row carries `partOfSpeech` / `posHint`.
 *
 * @param {ReturnType<typeof occurrenceLexiconFrequencyMeta>|null|undefined} meta
 * @param {Record<string, unknown>|null|undefined} overlayRow
 * @returns {ReturnType<typeof occurrenceLexiconFrequencyMeta>}
 */
export function mergeFrequencyMetaPos(meta, overlayRow) {
  if (!meta || typeof meta !== "object") return /** @type {any} */ (meta);
  if (!overlayRow || typeof overlayRow !== "object") return meta;
  var add = occurrencePosMeta(overlayRow);
  var ab0 = String(meta.posAbbrev || "").trim();
  var nt0 = String(meta.posNote || "").trim();
  var lk0 = String(meta.posLookupKey || "").trim();
  var ab1 = String(add.posAbbrev || "").trim();
  var nt1 = String(add.posNote || "").trim();
  var lk1 = String(add.posLookupKey || "").trim();
  var outAb = ab0 || ab1;
  var outNt = nt0 || nt1;
  var outLk = lk0 || lk1;
  if (outAb === ab0 && outNt === nt0 && outLk === lk0) return meta;
  return Object.assign({}, meta, { posAbbrev: outAb, posNote: outNt, posLookupKey: outLk });
}

/**
 * @param {unknown} zipf
 * @returns {number} band index 0..{@link ZIPF_BAND_LEVEL_KEYS}.length - 1, or -1 if not a finite score
 */
export function zipfToBand(zipf) {
  var z = Number(zipf);
  if (!Number.isFinite(z)) return -1;
  var t = getZipfThresholds();
  for (var i = 0; i < t.length; i++) {
    if (z >= t[i]) return i;
  }
  return t.length;
}

/**
 * @param {number} band 0 .. {@link ZIPF_BAND_LEVEL_KEYS}.length - 1
 * @returns {string} short Zipf interval for the legend
 */
export function zipfBandRangeLabel(band) {
  var b = Number(band) || 0;
  var t = getZipfThresholds();
  if (b === 0) return "Zipf ≥ " + t[0];
  if (b > 0 && b < t.length) return t[b] + " ≤ Zipf < " + t[b - 1];
  if (b === t.length) return "Zipf < " + t[t.length - 1];
  return "";
}
